import GroupMemberRecord from "models/view-models/group-member-record";
import { useState } from "react";
import useTeamOnboardingAlerts from "./use-team-onboarding-notifications";
import useCurrentIdentity from "utilities/hooks/use-current-identity";
import { CollectionUtils } from "utilities/collection-utils";
import StringUtils from "utilities/string-utils";
import { ToastManager } from "utilities/toast/toast-manager";

interface UseManagePendingInvitesHookOptions {
    totalLicenses: number;
    teamMembersToMigrate: GroupMemberRecord[];
    emailsToInvite: string[];
    onUpdateEmailsToInvite: (updatedEmails: string[]) => void;
    onEmailError: (error: string | undefined) => void;
    totalSeatsCurrentlyTaken: number;
}

const ERROR_INVALID_EMAILS = "ERROR_INVALID_EMAILS";
const ERROR_NOT_ENOUGH_SEATS = "ERROR_NOT_ENOUGH_SEATS";
const ERROR_NO_EMAILS_TO_INVITE = "ERROR_NO_EMAILS_TO_INVITE";
const ERROR_NO_EMAILS_ENTERED = "ERROR_NO_EMAILS_ENTERED";

// -----------------------------------------------------------------------------------------
// #region Hook
// -----------------------------------------------------------------------------------------

export function useManagePendingInvites(
    options: UseManagePendingInvitesHookOptions
) {
    const {
        totalLicenses,
        totalSeatsCurrentlyTaken,
        teamMembersToMigrate,
        emailsToInvite,
        onUpdateEmailsToInvite,
    } = options;
    const [inviteEmail, setInviteEmail] = useState<string>("");
    const { currentIdentity } = useCurrentIdentity();

    const {
        showNotEnoughSeatsAlert,
        showInvalidEmailAlert,
        showCannotInviteTeamOwnerWarning,
        showCannotHaveDuplicateTeamMembers,
    } = useTeamOnboardingAlerts();

    const handleOnChange = (e: React.ChangeEvent<HTMLTextAreaElement>) =>
        setInviteEmail(e.currentTarget.value);

    const removeTeamOwner = (incomingEmails: string[]) => {
        const teamOwnerEmail = currentIdentity?.user?.email!;
        return incomingEmails.filter((email) => email !== teamOwnerEmail!);
    };

    const getValidEmailsWithoutDuplicates = (emailsToAdd: string[]) => {
        if (CollectionUtils.isEmpty(emailsToAdd)) {
            throw ERROR_NO_EMAILS_ENTERED;
        }
        // Remove team owner and show error if needed
        const emailsWithoutTeamOwner = removeTeamOwner(emailsToAdd);

        // If removing the team owner means there are no more invites to send, show warning and throw no more emails to invite error
        if (CollectionUtils.isEmpty(emailsWithoutTeamOwner)) {
            showCannotInviteTeamOwnerWarning();
            throw ERROR_NO_EMAILS_TO_INVITE;
        }

        // Remove duplicates and show error if needed
        const uniqueEmails = removeDuplicateEmails(emailsWithoutTeamOwner, [
            ...teamMembersToMigrate.map((member) => member.email),
            ...emailsToInvite,
        ]);

        // If there are no emails left after removing duplicates
        if (CollectionUtils.isEmpty(uniqueEmails)) {
            showCannotHaveDuplicateTeamMembers();
            throw ERROR_NO_EMAILS_TO_INVITE;
        }
        // Make sure there are enough seats
        if (uniqueEmails.length + totalSeatsCurrentlyTaken > totalLicenses!) {
            throw ERROR_NOT_ENOUGH_SEATS;
        }

        // Validate emails. Show alert then throw if any are invalid
        const invalidEmails = getInvalidEmails(uniqueEmails);
        if (CollectionUtils.isNotEmpty(invalidEmails)) {
            showInvalidEmailAlert(invalidEmails.join(", "));
            throw ERROR_INVALID_EMAILS;
        }

        // Only show if the rest has succeeded
        if (emailsWithoutTeamOwner.length > uniqueEmails.length) {
            showCannotHaveDuplicateTeamMembers();
        }

        if (emailsToAdd.length > emailsWithoutTeamOwner.length) {
            showCannotInviteTeamOwnerWarning();
        }

        // Pass along unique emails
        return uniqueEmails;
    };

    const addEmailsToInviteList = (emailToAdd: string) => {
        const emailsToAdd = emailToAdd.split(",").map((e) => e.trim());
        try {
            const uniqueEmails = getValidEmailsWithoutDuplicates(emailsToAdd);
            onUpdateEmailsToInvite([...emailsToInvite, ...uniqueEmails]);
            setInviteEmail("");
        } catch (error) {
            switch (error) {
                case ERROR_NOT_ENOUGH_SEATS:
                    showNotEnoughSeatsAlert();
                    break;
                case ERROR_NO_EMAILS_TO_INVITE:
                    setInviteEmail("");
                    break;
                default:
                    console.log(error);
            }
        }
    };

    const handleAddClick = () => {
        ToastManager.dismissAll();
        addEmailsToInviteList(inviteEmail);
    };

    return {
        inviteEmail,
        setInviteEmail,
        handleAddClick,
        handleOnChange,
    };
}

// #endregion Hook

// -----------------------------------------------------------------------------------------
// #region Functions
// -----------------------------------------------------------------------------------------

const removeDuplicateEmails = (
    incomingEmails: string[],
    currentInvitations: string[]
): string[] => {
    return CollectionUtils.difference(
        CollectionUtils.distinct(incomingEmails),
        currentInvitations
    );
};

const getInvalidEmails = (emails: string[]): string[] => {
    const invalidEmails: string[] = [];
    emails.forEach((email) => {
        const isValid = StringUtils.isValidEmail(email);
        if (!isValid) {
            invalidEmails.push(email);
        }
    });
    return invalidEmails;
};

// #endregion Functions
